import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TemplateSection,
  createTemplateSection,
  updateTemplateSection,
  deleteTemplateSection
} from '@/services/supabase/templates';
import { templateKeys } from './useTemplateQueries';

// Create template section mutation
export function useCreateTemplateSectionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (section: {
      template_id: number | string;
      name: string;
      order_position?: number;
    }) => createTemplateSection(section),
    onSuccess: async (_, variables) => {
      // Ensure template_id is a number for consistent cache keys
      const templateId = Number(variables.template_id);

      // Force refetch template with fields query for immediate updates
      await queryClient.refetchQueries({
        queryKey: [...templateKeys.detail(templateId), 'fields']
      });

      // Also refetch template lists to update any template metadata
      await queryClient.refetchQueries({
        queryKey: templateKeys.lists()
      });
    },
  });
}

// Update template section mutation
export function useUpdateTemplateSectionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sectionId,
      data,
      templateId
    }: {
      sectionId: number | string;
      data: Partial<TemplateSection>;
      templateId: number | string;
    }) => updateTemplateSection(sectionId, data, templateId),
    onSuccess: async (_, variables) => {
      // Ensure templateId is a number for consistent cache keys
      const templateId = Number(variables.templateId);

      // Force refetch template with fields query for immediate updates
      await queryClient.refetchQueries({
        queryKey: [...templateKeys.detail(templateId), 'fields']
      });

      // Also refetch template lists to update any template metadata
      await queryClient.refetchQueries({
        queryKey: templateKeys.lists()
      });
    },
  });
}

// Delete template section mutation
export function useDeleteTemplateSectionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sectionId,
      templateId
    }: {
      sectionId: number | string;
      templateId: number | string;
    }) => deleteTemplateSection(sectionId, templateId),
    onSuccess: async (_, variables) => {
      // Ensure templateId is a number for consistent cache keys
      const templateId = Number(variables.templateId);

      // Force refetch template with fields query for immediate updates
      await queryClient.refetchQueries({
        queryKey: [...templateKeys.detail(templateId), 'fields']
      });

      // Also refetch template lists to update any template metadata
      await queryClient.refetchQueries({
        queryKey: templateKeys.lists()
      });
    },
  });
}
